import React, { useEffect, useState } from "react";
import { Package, Plus, Trash2, Loader2, ImagePlus, Pencil } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", price: "", features: "", image: null };

function readImage(file, cb) {
  if (!file) return;
  if (file.size > 4 * 1024 * 1024) return toast.error("حجم الصورة يتجاوز 4MB");
  const r = new FileReader();
  r.onload = () => cb(r.result);
  r.readAsDataURL(file);
}

export function ProductForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState(initial || EMPTY);
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 grid gap-3 sm:grid-cols-[120px_1fr]" data-testid="product-form">
      <label className="w-[120px] h-[120px] rounded-xl border-2 border-dashed border-border bg-secondary/40 flex items-center justify-center cursor-pointer overflow-hidden">
        <input type="file" accept="image/*" className="hidden" onChange={(e) => readImage(e.target.files?.[0], (img) => setF({ ...f, image: img }))} data-testid="product-image-input" />
        {f.image ? <img src={f.image} alt="" className="w-full h-full object-cover" /> : <span className="text-[11px] text-muted-foreground text-center px-2 flex flex-col items-center gap-1"><ImagePlus className="w-5 h-5" />صورة المنتج</span>}
      </label>
      <div className="grid gap-2">
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="اسم المنتج *" className="bg-secondary border-border" data-testid="product-name-input" />
        <Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="وصف مختصر: ما هو ولمن ولماذا يميّزه" className="bg-secondary border-border" data-testid="product-desc-input" />
        <div className="grid gap-2 sm:grid-cols-2">
          <Input value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} placeholder="السعر (اختياري) مثال: 149 ريال" className="bg-secondary border-border" data-testid="product-price-input" />
          <Input value={f.features} onChange={(e) => setF({ ...f, features: e.target.value })} placeholder="المزايا مفصولة بفاصلة: خامة فاخرة، ضمان سنتين" className="bg-secondary border-border" data-testid="product-features-input" />
        </div>
        <div className="flex gap-2 justify-end">
          {onCancel && <Button variant="ghost" size="sm" onClick={onCancel} data-testid="product-cancel-btn">إلغاء</Button>}
          <Button size="sm" disabled={saving || !f.name.trim()} className="bg-primary text-white gap-1" data-testid="product-save-btn"
            onClick={() => onSave({ name: f.name, description: f.description, price: f.price, image: f.image, features: f.features.split(/[،,]/).map((x) => x.trim()).filter(Boolean) })}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} حفظ المنتج
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ProductCatalog({ projectId, onChange }) {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/projects/${projectId}/products`).then((r) => { setItems(r.data); onChange?.(r.data); }).catch(() => {});
  useEffect(() => { if (projectId) load(); }, [projectId]); // eslint-disable-line

  const save = async (payload, id) => {
    setSaving(true);
    try {
      if (id) await api.put(`/products/${id}`, payload); else await api.post(`/projects/${projectId}/products`, payload);
      toast.success(id ? "تم تحديث المنتج" : "تمت إضافة المنتج — سيُستعرض في حملاتك");
      setAdding(false); setEditing(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const remove = async (id) => { await api.delete(`/products/${id}`); load(); };

  return (
    <div className="glass rounded-2xl p-5 border border-border" data-testid="product-catalog">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-primary" />
          <div>
            <h3 className="font-bold">منتجات العميل</h3>
            <p className="text-xs text-muted-foreground">أضف منتجاتك وصورها الحقيقية — تُستعرض في الحملات بلقطات بطل وأسلوب حياة احترافية، وبالاسم والمزايا كما أدخلتها.</p>
          </div>
        </div>
        {!adding && <Button size="sm" variant="outline" className="gap-1" onClick={() => { setAdding(true); setEditing(null); }} data-testid="add-product-btn"><Plus className="w-4 h-4" /> إضافة منتج</Button>}
      </div>
      {adding && <div className="mb-3"><ProductForm onSave={(p) => save(p)} onCancel={() => setAdding(false)} saving={saving} /></div>}
      {items.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => editing === p.id ? (
            <div key={p.id} className="sm:col-span-2 lg:col-span-3"><ProductForm initial={{ ...p, features: (p.features || []).join("، ") }} onSave={(d) => save(d, p.id)} onCancel={() => setEditing(null)} saving={saving} /></div>
          ) : (
            <div key={p.id} className="rounded-xl border border-border bg-secondary/40 p-3 flex gap-3" data-testid={`product-card-${p.id}`}>
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-background shrink-0 flex items-center justify-center">{p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : <Package className="w-6 h-6 text-muted-foreground" />}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-bold text-sm truncate">{p.name}</p>
                  <div className="flex gap-1 shrink-0">
                    <button type="button" onClick={() => setEditing(p.id)} className="text-muted-foreground hover:text-primary" data-testid={`edit-product-${p.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button type="button" onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-product-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                {p.price && <p className="text-xs text-primary font-bold">{p.price}</p>}
                {p.description && <p className="text-xs text-muted-foreground line-clamp-2">{p.description}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
      {!items.length && !adding && <p className="text-xs text-muted-foreground">لا توجد منتجات بعد.</p>}
    </div>
  );
}

export function ProductPicker({ products, selected, onChange }) {
  if (!products?.length) return null;
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div data-testid="campaign-product-picker">
      <p className="text-sm font-bold mb-1">المنتجات التي تستعرضها الحملة <span className="text-xs text-muted-foreground font-normal">(اختياري — كل منتج يأخذ لقطة بطل ومنشور أسلوب حياة)</span></p>
      <div className="flex flex-wrap gap-2">
        {products.map((p) => (
          <button key={p.id} type="button" onClick={() => toggle(p.id)} className={`flex items-center gap-2 rounded-full border px-2 py-1 text-xs transition ${selected.includes(p.id) ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary/40 hover:border-primary/40"}`} data-testid={`campaign-product-${p.id}`}>
            {p.image && <img src={p.image} alt="" className="w-5 h-5 rounded-full object-cover" />}{p.name}
          </button>
        ))}
      </div>
    </div>
  );
}
